/**
 * Notification panel for displaying in-app notifications.
 *
 * Features:
 * - Real-time updates via SSE
 * - Infinite scroll for loading more
 * - Priority-based styling
 * - Dynamic icons based on notification type
 * - Mark as read (individual and bulk)
 * - Archive notifications
 * - View All dialog
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  Archive,
  Bell,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  FileDown,
  Key,
  Loader2,
  Megaphone,
  MessageCircle,
  RefreshCw,
  Settings,
  Shield,
  Upload,
  UserCheck,
  UserMinus,
  UserPlus,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useNotificationStore,
  Notification,
} from '@/stores/notificationStore'
import { NotificationDialog } from './NotificationDialog'
import { cn } from '@/lib/utils'

interface NotificationPanelProps {
  onClose: () => void
}

// Map icon names from backend to Lucide icons
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Upload: Upload,
  CheckCircle: CheckCircle,
  XCircle: XCircle,
  UserPlus: UserPlus,
  UserMinus: UserMinus,
  Shield: Shield,
  Key: Key,
  Clock: Clock,
  AlertTriangle: AlertTriangle,
  Megaphone: Megaphone,
  AlertCircle: AlertCircle,
  UserCheck: UserCheck,
  RefreshCw: RefreshCw,
  MessageCircle: MessageCircle,
  Download: Download,
  FileDown: FileDown,
}

// Priority-based styling
const priorityStyles: Record<string, { bg: string; icon: string }> = {
  urgent: { bg: 'bg-red-50 dark:bg-red-950/30', icon: 'text-red-500' },
  high: { bg: 'bg-orange-50 dark:bg-orange-950/30', icon: 'text-orange-500' },
  normal: { bg: '', icon: 'text-blue-500' },
  low: { bg: '', icon: 'text-muted-foreground' },
}

interface NotificationItemProps {
  notification: Notification
}

function NotificationItem({ notification }: NotificationItemProps) {
  const markAsRead = useNotificationStore((state) => state.markAsRead)
  const archiveNotification = useNotificationStore((state) => state.archiveNotification)

  const Icon = notification.icon ? iconMap[notification.icon] : Bell
  const styles = priorityStyles[notification.priority] || priorityStyles.normal

  const handleClick = () => {
    if (!notification.isRead) {
      markAsRead(notification.id)
    }
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation()
    archiveNotification(notification.id)
  }

  return (
    <div
      className={cn(
        'group flex gap-3 p-3 hover:bg-accent transition-colors',
        !notification.isRead && 'bg-accent/50',
        styles.bg
      )}
      onClick={handleClick}
    >
      {Icon && (
        <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', styles.icon)} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <p className={cn('text-sm', !notification.isRead && 'font-semibold')}>
            {notification.title}
          </p>
          {!notification.isRead && (
            <span className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
          )}
        </div>
        {notification.message && (
          <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
            {notification.message}
          </p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {notification.timeAgo || 'Just now'}
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
        onClick={handleArchive}
        title="Archive"
      >
        <Archive className="h-3 w-3" />
        <span className="sr-only">Archive notification</span>
      </Button>
    </div>
  )
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
  const notifications = useNotificationStore((state) => state.notifications)
  const totalCount = useNotificationStore((state) => state.totalCount)
  const isLoading = useNotificationStore((state) => state.isLoading)
  const hasMore = useNotificationStore((state) => state.hasMore)
  const currentPage = useNotificationStore((state) => state.currentPage)
  const fetchNotifications = useNotificationStore((state) => state.fetchNotifications)
  const markAllAsRead = useNotificationStore((state) => state.markAllAsRead)

  const [showViewAllDialog, setShowViewAllDialog] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Fetch notifications on mount and mark all as read when panel opens
  useEffect(() => {
    fetchNotifications(1, true).then(() => {
      markAllAsRead()
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasMore && !isLoading) {
        fetchNotifications(currentPage + 1)
      }
    },
    [hasMore, isLoading, currentPage, fetchNotifications]
  )

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: scrollRef.current,
      rootMargin: '20px',
      threshold: 0,
    })

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [handleObserver])

  return (
    <div className="flex flex-col max-h-[min(450px,70vh)] w-80 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          <span className="font-semibold">Notifications</span>
        </div>
      </div>

      {/* Notification list */}
      {notifications.length === 0 && !isLoading ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Bell className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm font-medium">No notifications</p>
          <p className="text-xs text-muted-foreground">
            You're all caught up!
          </p>
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[350px]" ref={scrollRef}>
          <div className="divide-y">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
              />
            ))}
            {/* Load more trigger */}
            <div ref={loadMoreRef} className="h-1" />
            {isLoading && (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer with View All and settings link */}
      <div className="flex items-center justify-between p-2 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs gap-1"
          onClick={() => {
            onClose()
            // Navigate to notification settings
            window.location.href = '/account/notifications'
          }}
        >
          <Settings className="h-3 w-3" />
          Settings
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="text-xs gap-1"
          onClick={() => setShowViewAllDialog(true)}
        >
          <ExternalLink className="h-3 w-3" />
          View All
          {totalCount > 0 && (
            <span className="ml-1 text-muted-foreground">({totalCount})</span>
          )}
        </Button>
      </div>

      {/* View All Dialog */}
      <NotificationDialog
        open={showViewAllDialog}
        onOpenChange={(open) => {
          setShowViewAllDialog(open)
          if (!open) {
            // Refresh the panel when dialog closes
            fetchNotifications(1, true)
          }
        }}
      />
    </div>
  )
}
